/**
 * Curated mock data for the map tutorial
 *
 * The tutorial cannot rely on live data:
 *   - the marker WebSocket stream `/return/markers` is silent in the dev env
 *   - the matching backend is slow even when reachable
 * So we install a self-contained fixture set into `appState` when the tutorial
 * starts and remove it again when it closes. The fake `Matching` instance
 * bypasses the real {@link Matching} class entirely — no network traffic,
 * predictable timing.
 *
 * Lifecycle:
 *   - {@link installTutorialFixtures} called from {@link TutorialState.start}
 *   - {@link uninstallTutorialFixtures} called from {@link TutorialState.close}
 *   - {@link installFakeMatching} called from `_applyStepFlags('matching')`
 *   - {@link selectFakeMatch} called from `_applyStepFlags('route')`
 *
 * All fixture marker IDs are prefixed `tut-` so uninstall can target them
 * precisely without disturbing real data that may already be in the maps.
 */

import { SvelteMap, SvelteSet } from 'svelte/reactivity'
import type { MapMarker, WSMarkerResponse } from '$mapapp/MapApp/MapView/Marker/BasicMarker.model'
import {
	type LocationType,
	type Lot,
	type LotMatchPartner,
	type MatchOverviewData,
	OptimizerMailsStatus,
	type Pop,
	PopMaintenance,
	type PopMatchPartner,
	type PopProfile
} from '$mapapp/data/data.model'
import type { Matching } from '$mapapp/stores/matching.svelte'
import { MatchingResultsState } from '$mapapp/MapApp/SlideIn/Views/Matching/MatchingResults/matchingResultsState.svelte'
import { CalcMethod, LotState, LotType } from '$types/enums'
import { appState } from '$mapapp/stores/appstate.svelte'
import { get } from 'svelte/store'
import { userIsLoggedIn } from '$lib/stores'
// `routes_api` triggers a JWT fetch at module init time; import lazily inside
// {@link fetchTutorialRoute} so unit tests in node (TutorialState.test.ts) don't
// fail with "Invalid URL: /api/jwt" before they can mock the dependency chain.

const TUT_ID_PREFIX = 'tut'

/**
 * Map key format used by both Matching.matches and appState.allMarkers.
 *
 * Mirrors `markerIdFor()` in matching.svelte.ts:47. Duplicated here rather
 * than imported to avoid a circular dep between tutorial and matching.
 */
const markerIdFor = (objType: string, objId: string): string => `${objType}-${objId}`

/* ------------------------------------------------------------------ */
/* curated markers                                                    */
/* ------------------------------------------------------------------ */

/**
 * Map center used as the anchor for the fixture cluster.
 *
 * Coordinates are Stuttgart-area, chosen so the dev map (which centers on
 * Germany) shows the markers without zooming. The two cluster siblings are
 * < 10 m apart so MapLibre clusters them at any reasonable zoom; the match
 * partner sits ~5 km north-east, far enough not to cluster but close enough
 * for a readable route polyline.
 */
const ANCHOR_LON = 9.18
const ANCHOR_LAT = 48.78

const TUT_LOT_1: MapMarker = {
	markerId: 'lot-tutLot1',
	objectId: 'tutLot1',
	lat: ANCHOR_LAT,
	lon: ANCHOR_LON,
	type: 'lot',
	filterType: [LotType.DISPOSAL],
	filterState: [LotState.BIDDING],
	filterClassification: [],
	objectLabel: 'Demo Position 1',
	addressLabel: 'Stuttgart-Mitte',
	parentId: 'tutProjectA'
}

const TUT_LOT_2: MapMarker = {
	markerId: 'lot-tutLot2',
	objectId: 'tutLot2',
	// identical coords to TUT_LOT_1 — the global cluster source uses synthetic
	// "real-coincidence" grouping (cluster: false in LayerClusterMarker), so two
	// markers cluster only when their coordinates match exactly
	lat: ANCHOR_LAT,
	lon: ANCHOR_LON,
	type: 'lot',
	filterType: [LotType.DISPOSAL],
	filterState: [LotState.BIDDING],
	filterClassification: [],
	objectLabel: 'Demo Position 2',
	addressLabel: 'Stuttgart-Mitte',
	parentId: 'tutProjectA'
}

const TUT_LOT_3: MapMarker = {
	markerId: 'lot-tutLot3',
	objectId: 'tutLot3',
	lat: ANCHOR_LAT + 0.04,
	lon: ANCHOR_LON + 0.05,
	type: 'lot',
	filterType: [LotType.BACKFILL],
	filterState: [LotState.BIDDING],
	filterClassification: [],
	objectLabel: 'Demo Annahmestelle',
	addressLabel: 'Stuttgart-Ost',
	parentId: 'tutProjectB'
}

const TUT_POP_1: MapMarker = {
	markerId: 'pop-tutPop1',
	objectId: 'tutPop1',
	lat: ANCHOR_LAT + 0.025,
	lon: ANCHOR_LON - 0.02,
	type: 'pop',
	// codes must match real `LocationType.code` values from `/api/map/locationTypes` so
	// `getPopColor` resolves the right colorHex (otherwise fallback → black marker).
	filterType: ['landfill'],
	filterState: [],
	filterClassification: [],
	objectLabel: 'Demo Deponie',
	addressLabel: 'Stuttgart-West'
}

const TUT_POP_2: MapMarker = {
	markerId: 'pop-tutPop2',
	objectId: 'tutPop2',
	lat: ANCHOR_LAT - 0.03,
	lon: ANCHOR_LON + 0.04,
	type: 'pop',
	filterType: ['construction-mat-recycling'],
	filterState: [],
	filterClassification: [],
	objectLabel: 'Demo Recyclinghof',
	addressLabel: 'Stuttgart-Süd'
}

/**
 * Standalone DISPOSAL lot used by the marker step.
 *
 * Sits clear of the cluster pair (TUT_LOT_1/2) so `_resolveStepData()` picks it
 * as the spotlight marker — its rule is "first DISPOSAL lot in viewport that is
 * NOT part of the cluster pair".
 */
const TUT_LOT_MARKER: MapMarker = {
	markerId: 'lot-tutLotMarker',
	objectId: 'tutLotMarker',
	lat: ANCHOR_LAT - 0.02,
	lon: ANCHOR_LON - 0.04,
	type: 'lot',
	filterType: [LotType.DISPOSAL],
	filterState: [LotState.BIDDING],
	filterClassification: ['Z1.1'],
	objectLabel: 'Entsorgung | 2.000 t | 170504',
	addressLabel: '70173 Stuttgart',
	parentId: 'tutProjectC'
}

/**
 * Standalone Pop used by the marker step (Schritt 5). The slide-in Pop view
 * is richer than Lot (profiles, materials, contact card) — the tutorial highlights
 * what users actually see in the panel, not a sparse demo lot.
 */
const TUT_POP_MARKER: MapMarker = {
	markerId: 'pop-tutPopMarker',
	objectId: 'tutPopMarker',
	lat: ANCHOR_LAT + 0.01,
	lon: ANCHOR_LON + 0.02,
	type: 'pop',
	filterType: ['landfill'],
	filterState: [],
	filterClassification: ['Z1.1', 'Z1.2'],
	objectLabel: 'Deponie Stuttgart-Mitte',
	addressLabel: 'Königstraße 1, 70173 Stuttgart'
}

const TUT_MARKERS: readonly MapMarker[] = [
	TUT_LOT_1,
	TUT_LOT_2,
	TUT_LOT_3,
	TUT_LOT_MARKER,
	TUT_POP_1,
	TUT_POP_2,
	TUT_POP_MARKER
]

/* ------------------------------------------------------------------ */
/* curated lot details (returned by getLotInformation interception)   */
/* ------------------------------------------------------------------ */

const tutLotDetail = (id: string, label: string, type: LotType, pricePerTon: number): Lot => ({
	id,
	pricePerTon,
	volume: 250,
	correlationId: label,
	label,
	code: label,
	type,
	lotState: LotState.BIDDING,
	calcMethod: CalcMethod.NORMAL,
	timeframe: '2026 Q2',
	address: 'Königstraße 1, 70173 Stuttgart',
	requesterSiteLocation: {
		street: 'Königstraße',
		houseNumber: '1',
		city: 'Stuttgart',
		zip: '70173'
	},
	offerSiteLocation: null,
	contact: { name: 'Demo Kontakt', email: 'demo@example.org' },
	materialProperties: [],
	attachments: [],
	materialName: 'Demo Material',
	recyclingClasses: ['Z1.1'],
	optimizerMailsStatus: OptimizerMailsStatus.NONE,
	comments: []
})

// Rich detail used by the marker step (Schritt 5). Heftier values than the
// shared tutLotDetail factory so the slide-in panel renders with realistic
// volume / material / class / contact / attachments instead of empty cells.
const TUT_LOT_MARKER_DETAIL: Lot = {
	id: 'tutLotMarker',
	pricePerTon: 18,
	volume: 2000,
	correlationId: 'P-2026-DEMO-042',
	label: 'Entsorgung von 5.000 Tonnen voll schönem Boden #beauty',
	code: '170504',
	type: LotType.DISPOSAL,
	lotState: LotState.BIDDING,
	calcMethod: CalcMethod.NORMAL,
	timeframe: '2026 Q3 – Q4',
	address: 'Königstraße 1, 70173 Stuttgart',
	requesterSiteLocation: {
		street: 'Königstraße',
		houseNumber: '1',
		city: 'Stuttgart',
		zip: '70173'
	},
	offerSiteLocation: null,
	contact: {
		name: 'Anna Müller',
		email: 'anna.mueller@demo.org',
		phone: '+49 711 1234567',
		mobile: '+49 170 7654321'
	},
	materialProperties: [],
	attachments: [],
	materialName: 'Bauschutt mineralisch',
	recyclingClasses: ['Z1.1', 'Z1.2'],
	optimizerMailsStatus: OptimizerMailsStatus.NONE,
	comments: []
}

const TUT_LOT_DETAILS = new Map<string, Lot>([
	['tutLot1', tutLotDetail('tutLot1', 'SA-tut-1', LotType.DISPOSAL, 18)],
	['tutLot2', tutLotDetail('tutLot2', 'SA-tut-2', LotType.DISPOSAL, 18)],
	['tutLot3', tutLotDetail('tutLot3', 'SA-tut-3', LotType.BACKFILL, 22)],
	['tutLotMarker', TUT_LOT_MARKER_DETAIL]
])

/**
 * Lookup fixture lot detail by object id. Returns undefined for non-tutorial ids
 * so callers (e.g. `getLotInformation`) can fall through to the real network call.
 * @public
 */
export function getTutorialLotDetail(lotId: string): Lot | undefined {
	if (!lotId.startsWith(TUT_ID_PREFIX)) return undefined
	return TUT_LOT_DETAILS.get(lotId)
}

/* ------------------------------------------------------------------ */
/* curated pop detail (marker step)                                   */
/* ------------------------------------------------------------------ */

const TUT_POP_LOCATION_TYPE: LocationType = {
	code: 'landfill',
	colorHex: '#46C6E3',
	id: 'tut-loc-deponie-marker',
	shortText: 'Deponie',
	priority: 0
}

const TUT_POP_MARKER_DETAIL: Pop = {
	id: 'tutPopMarker',
	label: 'Deponie Stuttgart-Mitte',
	ownSite: false,
	types: [TUT_POP_LOCATION_TYPE],
	address: 'Königstraße 1, 70173 Stuttgart',
	maintained: PopMaintenance.MAINTAINED,
	maintainedDate: '2026-04-15',
	popType: 'POP',
	contact: {
		name: 'Anna Müller',
		email: 'anna.mueller@demo.org',
		phone: '+49 711 1234567',
		mobile: '+49 170 7654321'
	},
	attachments: [],
	comments: [],
	// Profile IDs (with `profile-` prefix) — ViewList strips the prefix and feeds
	// the bare id to Profile.svelte which fetches via getProfileInformation. The
	// fixture short-circuit in data.svelte.ts intercepts ids prefixed `tut`.
	materialProvides: ['profile-tutProvide1', 'profile-tutProvide2'],
	materialAccepts: ['profile-tutAccept1', 'profile-tutAccept2', 'profile-tutAccept3'],
	tenantId: 'tut-tenant'
}

const tutProfile = (
	id: string,
	label: string,
	profileType: 'ACCEPT_MATERIAL' | 'PROVIDE_MATERIAL',
	pricePerTon: number
): PopProfile => ({
	id,
	label,
	profileType,
	materialLabel: '170504',
	materialProperties: [],
	attachments: [],
	comments: [],
	pricePerTon,
	popId: 'tutPopMarker'
})

const TUT_PROFILE_DETAILS = new Map<string, PopProfile>([
	['tutAccept1', tutProfile('tutAccept1', 'Bauschutt mineralisch', 'ACCEPT_MATERIAL', 14)],
	['tutAccept2', tutProfile('tutAccept2', 'Boden Z0/Z1.1', 'ACCEPT_MATERIAL', 9)],
	['tutAccept3', tutProfile('tutAccept3', 'Straßenaufbruch', 'ACCEPT_MATERIAL', 12)],
	['tutProvide1', tutProfile('tutProvide1', 'Recyclingbeton 0/45', 'PROVIDE_MATERIAL', 7)],
	['tutProvide2', tutProfile('tutProvide2', 'Gleisschotter', 'PROVIDE_MATERIAL', 11)]
])

/**
 * Lookup fixture profile detail by id. Returns undefined for non-tutorial ids
 * so callers (e.g. `getProfileInformation`) can fall through to the real network call.
 * @public
 */
export function getTutorialProfileDetail(profileId: string): PopProfile | undefined {
	if (!profileId.startsWith(TUT_ID_PREFIX)) return undefined
	return TUT_PROFILE_DETAILS.get(profileId)
}

// Match-partner pop shown in the matching step. Reuses the marker pop's detail (same deponie
// shape; every referenced profile is fixture-backed) so `getPopInformation('tutPop1')`
// short-circuits the network — anonymous users would otherwise 404 → unhandled JSON rejection.
const TUT_POP_1_DETAIL: Pop = { ...TUT_POP_MARKER_DETAIL, id: 'tutPop1', label: 'Demo Deponie' }
const TUT_POP_DETAILS = new Map<string, Pop>([
	['tutPopMarker', TUT_POP_MARKER_DETAIL],
	['tutPop1', TUT_POP_1_DETAIL]
])

/**
 * Lookup fixture pop detail by object id. Returns undefined for non-tutorial ids
 * so callers (e.g. `getPopInformation`) can fall through to the real network call.
 * @public
 */
export function getTutorialPopDetail(popId: string): Pop | undefined {
	if (!popId.startsWith(TUT_ID_PREFIX)) return undefined
	return TUT_POP_DETAILS.get(popId)
}

/* ------------------------------------------------------------------ */
/* curated matches                                                    */
/* ------------------------------------------------------------------ */

const lotPartner = (objId: string, label: string): LotMatchPartner => ({
	objId,
	objType: 'lot',
	label,
	providesMaterial: false,
	lotState: LotState.BIDDING,
	type: LotType.BACKFILL,
	calcMethod: CalcMethod.NORMAL,
	// LotHeaderProps fields read by LotHeader (addressDataToString crashes on undefined location)
	volume: 250,
	materialName: 'Demo Material',
	recyclingClasses: ['Z1.1'],
	timeframe: '2026 Q2',
	address: 'Stuttgart-Ost',
	requesterSiteLocation: {
		city: 'Stuttgart',
		zip: '70173',
		street: 'Demo-Straße',
		houseNumber: '12'
	},
	offerSiteLocation: null,
	optimizerMailsStatus: OptimizerMailsStatus.NONE,
	pricePerTon: 22
})

const TUT_LOCATION_TYPE_DEPONIE: LocationType = {
	code: 'landfill',
	colorHex: '#46C6E3',
	id: 'tut-loc-deponie',
	shortText: 'Deponie',
	priority: 0
}

const popPartner = (objId: string, label: string): PopMatchPartner => ({
	objId,
	objType: 'pop',
	label,
	providesMaterial: false,
	types: [TUT_LOCATION_TYPE_DEPONIE],
	ownSite: false,
	matchingProfileId: 'tut-profile',
	matchingProfileName: 'Demo Profil',
	address: 'Stuttgart-West',
	maintained: PopMaintenance.MAINTAINED,
	maintainedDate: '2026-05-01'
})

const TUT_MATCH_LOT: MatchOverviewData = {
	partner: lotPartner('tutLot3', 'Demo Annahmestelle'),
	circulationInformation: {
		oneWayTime: 34,
		oneWayDistance: 34,
		circularTime: 98,
		circularDistance: 68,
		co2Emission: 2.45
	},
	priceInformation: {
		pricePerTon: 30.75,
		transportCost: { vehicleTransportCosts: null, flatrateTransportCosts: 4.76 },
		partnerPricePerTon: '25.99',
		volume: 120
	},
	conditionalMatch: false,
	substituteMatch: false,
	underlyingAnalytical: { id: 'tut-analytical-1', name: 'Demo Analyse', technicalName: 'DEMO_1' },
	recommendation: '',
	catchmentAreaRequirement: false,
	platformRequestPossible: false
}

const TUT_MATCH_POP: MatchOverviewData = {
	partner: popPartner('tutPop1', 'Demo Deponie'),
	circulationInformation: {
		oneWayTime: 22,
		oneWayDistance: 18,
		circularTime: 64,
		circularDistance: 36,
		co2Emission: 1.32
	},
	priceInformation: {
		pricePerTon: 28.5,
		transportCost: { vehicleTransportCosts: null, flatrateTransportCosts: 3.5 },
		partnerPricePerTon: '25.0',
		volume: 80
	},
	conditionalMatch: true,
	substituteMatch: false,
	underlyingAnalytical: { id: 'tut-analytical-2', name: 'Demo Analyse 2', technicalName: 'DEMO_2' },
	recommendation: '',
	catchmentAreaRequirement: false,
	platformRequestPossible: false
}

/* ------------------------------------------------------------------ */
/* curated route polyline                                             */
/* ------------------------------------------------------------------ */

/**
 * fallback polyline (straight line between marker and partner).
 *
 * Used only if {@link fetchTutorialRoute} fails — normally we ask the real
 * Google Routes API for a road-following polyline. The straight-line variant
 * is visually wrong but better than nothing.
 */
const TUT_ROUTE_FALLBACK: [number, number][] = (() => {
	const points: [number, number][] = []
	const STEPS = 8
	for (let i = 0; i <= STEPS; i++) {
		const t = i / STEPS
		points.push([
			TUT_LOT_MARKER.lon + (TUT_LOT_3.lon - TUT_LOT_MARKER.lon) * t,
			TUT_LOT_MARKER.lat + (TUT_LOT_3.lat - TUT_LOT_MARKER.lat) * t
		])
	}
	return points
})()

/** Cached road-following polyline; populated on first {@link selectFakeMatch} call. */
let cachedRoutePolyline: [number, number][] | null = null

async function fetchTutorialRoute(): Promise<[number, number][]> {
	if (cachedRoutePolyline) return cachedRoutePolyline
	// Anonymous users can't reach the Google Routes proxy (needs auth) — the request 400s and
	// logs a console error even though we fall back. Use the straight-line fixture directly for
	// them; keep the road-following lookup for logged-in users (prod has a valid key).
	if (!get(userIsLoggedIn)) {
		return TUT_ROUTE_FALLBACK
	}

	try {
		const { computePolylineRoute } = await import('$mapapp/utils/routes_api')
		const { polyline } = await computePolylineRoute(
			{ lat: TUT_LOT_MARKER.lat, lon: TUT_LOT_MARKER.lon },
			{ lat: TUT_LOT_3.lat, lon: TUT_LOT_3.lon }
		)
		if (Array.isArray(polyline) && polyline.length >= 2) {
			cachedRoutePolyline = polyline as [number, number][]
			return cachedRoutePolyline
		}
	} catch (err) {
		if (import.meta.env.DEV) console.warn('[tutorial] route fetch failed, using fallback', err)
	}
	return TUT_ROUTE_FALLBACK
}

/* ------------------------------------------------------------------ */
/* fake Matching                                                      */
/* ------------------------------------------------------------------ */

let activeFakeMatching: FakeMatching | null = null

/**
 * Reactive fake of the real Matching class.
 *
 * Fields read by the UI (`selectedMatch`, `fsm.current`, `radius.from/to`, …)
 * are declared with `$state` so the UI re-renders when {@link selectFakeMatch}
 * mutates them. Methods that the real class exposes but the tutorial never
 * exercises (network refetch, route retry, fit-to-radius) are no-ops.
 */
// exported so the drift guard in tutorialFixtures.test.ts can diff its surface against the
// real Matching; the app only instantiates it via installFakeMatching below.
export class FakeMatching {
	matches = $state(new SvelteMap<string, MatchOverviewData>())
	matchesLoading = $state(false)
	selectedMatch: MatchOverviewData | undefined = $state(undefined)
	radius = $state({ from: 0, to: 30 })
	confirmedRadius = $state({ from: 0, to: 30 })
	referenceMarker: MapMarker | undefined = $state(TUT_LOT_MARKER)
	// --- consumer-read surface mirrored from the real Matching (matching.svelte.ts) ---
	// read by LayerClusterMarker / SlideIn / MarkerShell / LayerMatching on `appState.matching`;
	// the drift guard in tutorialFixtures.test.ts enforces parity with the real class.
	temp_selectedId: string | null = $state(null)
	temp_selectedMarker: MapMarker | undefined = $state(undefined)
	temp_selectedType: MapMarker['type'] | 'cluster' | undefined = $state(undefined)
	temp_selectedChildrenIds: string[] = $state([])
	temp_selectedChildren: MapMarker[] | undefined = $state(undefined)
	autoSelectFirst: boolean = $state(true)
	selectedMatchId: string | undefined = $derived.by(() =>
		this.selectedMatch
			? markerIdFor(this.selectedMatch.partner.objType, this.selectedMatch.partner.objId)
			: undefined
	)
	matchMarkerIds = $derived(
		new SvelteSet(
			Array.from(this.matches.values(), (m) => markerIdFor(m.partner.objType, m.partner.objId))
		)
	)
	matchMarkers = $derived(
		Array.from(this.matches.values(), (m) =>
			appState.allMarkers.get(markerIdFor(m.partner.objType, m.partner.objId))
		)
	)
	matchReferenceData: LotMatchPartner = $state({
		objId: 'tutLotMarker',
		objType: 'lot',
		label: 'SA-tut-marker',
		providesMaterial: true,
		lotState: LotState.BIDDING,
		type: LotType.DISPOSAL,
		calcMethod: CalcMethod.NORMAL,
		volume: 250,
		materialName: 'Demo Material',
		recyclingClasses: ['Z1.1'],
		timeframe: '2026 Q2',
		address: 'Stuttgart-Süd',
		requesterSiteLocation: {
			city: 'Stuttgart',
			zip: '70173',
			street: 'Königstraße',
			houseNumber: '1'
		},
		offerSiteLocation: null,
		pricePerTon: 18,
		optimizerMailsStatus: OptimizerMailsStatus.NONE
	})
	/**
	 * Mirrors {@link Matching.resultsState} — the shared ranking source `LayerMatching` reads
	 * as `matching?.resultsState.rankByMarkerId` to number the map badges. Wired to the fake's
	 * own matches + reference so the tutorial map ranks exactly like production.
	 */
	resultsState = new MatchingResultsState(
		() => this.matches,
		() => this.matchReferenceData
	)
	returnFreights = $state(new SvelteMap<string, unknown>())
	returnFreightMarkers: (MapMarker | undefined)[] = $state([])
	miniLot = undefined
	partnerMailInfoCache = $state(new SvelteMap<string, Promise<unknown>>())
	history: { log: unknown[] } = { log: [] }
	fsm = $state<{
		current: 'results' | 'result'
		send: (event: string, payload?: { id: string; type: string }) => void
	}>({
		current: 'results',
		send: (event, payload) => {
			if (event === 'set_result' && payload) {
				const key = markerIdFor(payload.type, payload.id)
				const match = this.matches.get(key)
				if (match) {
					this.selectedMatch = match
					this.fsm.current = 'result'
				}
			} else if (event === 'set_results') {
				this.selectedMatch = undefined
				this.fsm.current = 'results'
			}
		}
	})
	pendingRouteMatch = null
	/**
	 * Mirrors {@link Matching.routingInfoVisible} — read by `RouteInfoOverlay` and written by
	 * `LayerRouting` on `appState.matching`. The tutorial route step renders both, so the fake
	 * must carry the flag or the drift guard (tutorialFixtures.test.ts) fails.
	 */
	routingInfoVisible = $state(false)

	constructor() {
		this.matches.set(markerIdFor('lot', 'tutLot3'), TUT_MATCH_LOT)
		this.matches.set(markerIdFor('pop', 'tutPop1'), TUT_MATCH_POP)
	}

	destroy() {
		/* nothing to destroy — no subscriptions, no effects */
	}
	/**
	 * Mirrors {@link Matching.fitToRadius} — reads the bounds of the maplibre
	 * source rendered by `LayerMatchingRadius` and fits the map viewport to it.
	 * Without this the tutorial's matching step would keep the previous step's
	 * camera (filter step) and the reference marker + radius circle stay
	 * off-screen.
	 */
	async fitToRadius(): Promise<boolean> {
		const map = appState.map
		if (!map) return false
		// matches the source id LayerMatchingRadius registers
		const source = map.getSource('matching-radius-src') as
			{ getBounds: () => Promise<{ isEmpty: () => boolean }> } | undefined
		if (!source) return false
		const bounds = await source.getBounds()
		if (bounds.isEmpty()) return false
		// fit instantly so the tutorial step transition doesn't stall on a camera animation
		appState.handleFitBounds(bounds as never, { padding: 20, animate: false }, true)
		return true
	}
	isRadiusPending = $state(false)
	markRadiusEdited() {
		this.isRadiusPending = true
	}
	retryPendingRoute() {
		/* no pending route — synchronous fixture flow */
	}
	/**
	 * Mirrors {@link Matching.recenterRoute} — called by `SecondarySlideIn` when the secondary
	 * panel collapses/expands. The tutorial's route is a static fixture polyline, so there's no
	 * camera re-fit to do; the method exists purely so `appState.matching?.recenterRoute()` on
	 * the fake is callable rather than undefined.
	 */
	recenterRoute(_secondaryWidthPx?: number): void {
		/* tutorial route is a fixed fixture — no dynamic camera re-fit */
	}
	refetch() {
		/* no remote source to re-query */
	}
	hasNextMatch() {
		return false
	}
	setNextMatch() {
		/* no-op for the tutorial — match list is fixed */
	}
	setReturnFreights(_ids: string[]) {
		/* tutorial doesn't model return-freight scenarios */
	}
	handleMatchingHistory() {
		/* tutorial owns navigation via tutorialState — no matching history to restore */
	}
	// mirror real Matching.closeSelectedMatch (matching.svelte.ts) — clear the selection and
	// return to the results list; autoSelectFirst stays false so it isn't re-selected.
	closeSelectedMatch() {
		this.autoSelectFirst = false
		this.selectedMatch = undefined
		this.fsm.current = 'results'
	}
	async getPartnerMailInfo(_id: string, _force?: boolean) {
		return { isRequested: false, toolTipText: '' }
	}
}

/* ------------------------------------------------------------------ */
/* install / uninstall                                                */
/* ------------------------------------------------------------------ */

let installed = false

/**
 * Write fixture markers into appState. Idempotent.
 *
 * Does not touch `appState.matching` — that is installed on demand by
 * {@link installFakeMatching} when the matching step is entered.
 */
export function installTutorialFixtures(): void {
	if (installed) return
	installed = true

	for (const marker of TUT_MARKERS) {
		// route through the same dispatch the WebSocket subscription uses so any
		// downstream derived/effect picks up the writes identically
		const response: WSMarkerResponse = { markers: [marker] }
		applyMarkerWrite(response.markers[0])
	}
}

function applyMarkerWrite(marker: MapMarker): void {
	if (marker.type === 'lot') {
		appState.lots.set(marker.markerId, marker)
		if (marker.parentId) {
			const existing = appState.projectLots.get(marker.parentId) ?? []
			existing.push(marker)
			appState.projectLots.set(marker.parentId, [...existing])
		}
	} else if (marker.type === 'pop') {
		appState.pops.set(marker.markerId, marker)
	}
	appState.allMarkers.set(marker.markerId, marker)
}

/**
 * Remove every fixture marker, the fake matching object, and the demo route.
 * Safe to call multiple times.
 */
export function uninstallTutorialFixtures(): void {
	if (!installed) return
	installed = false

	for (const marker of TUT_MARKERS) {
		if (marker.type === 'lot') {
			appState.lots.delete(marker.markerId)
			if (marker.parentId) {
				const siblings = (appState.projectLots.get(marker.parentId) ?? []).filter(
					(m) => m.markerId !== marker.markerId
				)
				if (siblings.length === 0) {
					appState.projectLots.delete(marker.parentId)
				} else {
					appState.projectLots.set(marker.parentId, siblings)
				}
			}
		} else if (marker.type === 'pop') {
			appState.pops.delete(marker.markerId)
		}
		appState.allMarkers.delete(marker.markerId)
	}

	if (activeFakeMatching && appState.matching === (activeFakeMatching as unknown as Matching)) {
		appState.matching = null
		activeFakeMatching = null
	}

	appState.route.clearCoordinates()
}

/**
 * Replace `appState.matching` with a fake instance pre-loaded with fixture
 * matches. Drives the underlying FSM into the matching state without going
 * through `appState.startMatching`, so the slow backend is never contacted
 * and the map flyTo is skipped.
 */
export function installFakeMatching(): void {
	activeFakeMatching = new FakeMatching()
	if (appState.fsm.current !== 'matching') {
		// 1. select the reference lot first so LayerMatching can render its on-map pin
		//    (LayerMatching reads `appState.selectedMarker` for the matching origin)
		appState.fsm.send('set_lot', { id: 'lot-tutLotMarker', flyTo: false })
		// 2. enter the matching state — initializeMatching constructs a real Matching
		//    instance which we immediately destroy + replace with the fake. The real
		//    construction is cheap when the WebSocket call below it doesn't matter
		//    (we never wait for /return/matches).
		appState.fsm.send('set_matching', { id: 'tutLotMarker', type: 'lot', flyTo: false })
		appState.matching?.destroy?.()
	}
	appState.matching = activeFakeMatching as unknown as Matching
}

/**
 * Select the first fixture match (the lot partner) and seed the route polyline.
 *
 * Mirrors the user clicking a row in the matching table. The route polyline
 * is fetched from the real Google Routes API (cached after first call) so the
 * line follows actual roads; a straight-line fallback is used if the request
 * fails (no JWT key, offline, etc).
 */
export function selectFakeMatch(): void {
	if (!activeFakeMatching) return
	activeFakeMatching.fsm.send('set_result', { id: 'tutLot3', type: 'lot' })
	// Seed the fallback immediately so the layer has something to render, then
	// upgrade to the road-following polyline once the API responds.
	appState.route.setCoordinates(TUT_ROUTE_FALLBACK)
	void fetchTutorialRoute().then((polyline) => {
		// only apply if the route step is still active — guards against late
		// arrivals after the user navigated away
		if (activeFakeMatching?.selectedMatch) {
			appState.route.setCoordinates(polyline)
		}
	})
}
